import { useLocalObservable } from 'mobx-react-lite';

import { IImageLoadHandlerParams } from '@gem/components';
import { useTranslation } from '@gem/modules/i18n';
import { clearPhoneNumber, parsePhone } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { TAvatarBackgroundColorIndex } from 'APP/components/Avatar/Avatar';
import { ImageCropperPopup } from 'APP/main/PopupManager/Popups/ImageCropperPopup/ImageCropperPopup';
import Entities from 'STORE';

export const MIN_LENGTH_MIDDLE_NAME = 3;

export interface IEditProfileFormData {
  avatarUrl: string;
  nickName: string;
  phone: string;
  position: string;
  firstName: string;
  lastName: string;
  middleName: string;
  displayName: string;
}

interface IEditProfileFormPresenter {
  defaultValues: IEditProfileFormData;
  avatarTitle: string;
  avatarColorIndex: TAvatarBackgroundColorIndex;
  allowUserProfileEdit: boolean;
  allowUserDisplayNameEdit: boolean;
  requireMiddleName: boolean;
  getNicknameFieldText(isValidating: boolean, isDirty: boolean, error?: string): string;
  parsePhoneNumber(number: string): string;
  onImageLoad(params: IImageLoadHandlerParams): void;
}

export function useEditProfileFormPresenter(): IEditProfileFormPresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IEditProfileFormPresenter>(() => ({
    defaultValues: {
      avatarUrl: Entities.usersStore.Me.avatarUrl || '',
      nickName: Entities.usersStore.Me.nickName,
      phone: Entities.usersStore.Me.displayPhone || '',
      position: Entities.usersStore.Me.position,
      firstName: Entities.usersStore.Me.firstName,
      lastName: Entities.usersStore.Me.lastName,
      middleName: Entities.usersStore.Me.middleName,
      displayName: Entities.usersStore.Me.displayName,
    },

    get avatarTitle(): string {
      return Entities.usersStore.Me.avatarTitle;
    },

    get avatarColorIndex(): TAvatarBackgroundColorIndex {
      return Entities.usersStore.Me.avatarColorIndex;
    },

    get allowUserProfileEdit(): boolean {
      return Entities.workspace.workspaceSetting.allowUserProfileEdit;
    },

    get allowUserDisplayNameEdit(): boolean {
      return Entities.workspace.workspaceSetting.allowUserDisplayNameEdit;
    },

    get requireMiddleName(): boolean {
      return (
        Entities.workspace.workspaceSetting.requireMiddleName && presenter.allowUserProfileEdit
      );
    },

    getNicknameFieldText(isValidating, isDirty, error?: string): string {
      if (isValidating) {
        return t('checking_nickname');
      } else if (error) {
        return error;
      } else {
        return !isDirty ? t('nickanme_hint') : t('nickname_is_available');
      }
    },

    parsePhoneNumber(phoneNumber: string): string {
      const number = clearPhoneNumber(phoneNumber);
      const country = Entities.App.availableCountries.find(({ phonePrefix }) => {
        return `+${number}`.indexOf(phonePrefix) === 0;
      });
      return parsePhone(number, country?.phoneTemplate);
    },

    onImageLoad(params: IImageLoadHandlerParams): void {
      Tasks.app.openPopup(ImageCropperPopup, { params });
    },
  }));

  return presenter;
}
